import React from 'react';
import { 
  BarChart, 
  LineChart, 
  AreaChart, 
  DonutChart, 
  Card,
  Title,
  Text,
  Grid,
  Flex,
  ProgressBar,
  Callout,
  Bold,
  TabGroup,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Legend,
  Color
} from '@tremor/react';
import { InformationCircleIcon, CheckCircleIcon } from '@heroicons/react/24/solid';
import { ChartData } from '../../utils/blogTypes';

interface BlogChartProps {
  chartData: ChartData;
}

export default function BlogChart({ chartData }: BlogChartProps) {
  const { type, title, description, data, categories, index, colors, valueFormatter } = chartData;

  // Format for currency if needed
  const formatter = valueFormatter === 'currency' 
    ? (value: number) => `$${value.toLocaleString()}`
    : undefined;

  const renderChart = () => {
    switch (type) {
      case 'bar':
        return (
          <BarChart
            data={data}
            index={index || ''}
            categories={categories || []}
            colors={colors || ['blue']}
            valueFormatter={formatter}
            className="mt-4 h-80"
          />
        );
      case 'line':
        return (
          <LineChart
            data={data}
            index={index || ''}
            categories={categories || []}
            colors={colors || ['blue']}
            valueFormatter={formatter}
            className="mt-4 h-80"
          />
        );
      case 'area':
        return (
          <AreaChart
            data={data}
            index={index || ''}
            categories={categories || []}
            colors={colors || ['blue']}
            valueFormatter={formatter}
            className="mt-4 h-80"
          />
        );
      case 'pie':
        return (
          <DonutChart
            data={data}
            index={index || 'name'}
            category={categories?.[0] || 'value'}
            colors={colors || ['blue', 'cyan', 'indigo']}
            valueFormatter={formatter}
            className="mt-4 h-80"
          />
        );
      case 'donut':
        return (
          <DonutChart
            data={data}
            index={index || 'name'}
            category={categories?.[0] || 'value'}
            colors={colors || ['blue', 'cyan', 'indigo']}
            valueFormatter={formatter}
            className="mt-4 h-80"
          />
        );
      default:
        return <div className="text-red-500">Chart type not supported</div>;
    }
  };

  return (
    <Card className="my-6 bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow rounded-lg">
      <Title className="text-gray-900">{title}</Title>
      {description && <Text className="text-gray-700 mt-2">{description}</Text>}
      {renderChart()}
    </Card>
  );
}
